import type { SlipSize } from './schemas/print-job';
import type { PrintTemplateDocument } from './schemas/print-template';

/**
 * Page geometry: millimetres, paper sizes, and how tall the bands have to be.
 *
 * In `shared/` because the renderer and the template builder must agree to the
 * point. The builder tells you a header band is too short for the letterhead
 * you have configured; the renderer makes sure it is not, whatever the number
 * says. Two implementations of that arithmetic would eventually disagree, and
 * the symptom — a field quietly missing from the printed letterhead — is one
 * nobody reports as a bug because the sheet still looks plausible.
 */

const PT_PER_MM = 72 / 25.4;

/** Millimetres in, PostScript points out. */
export function mm(value: number): number {
  return value * PT_PER_MM;
}

/** CSS pixels in, PostScript points out — 96 px to the inch. */
export function px(value: number): number {
  return value * 0.75;
}

/** Points in, millimetres out. */
export function toMm(points: number): number {
  return points / PT_PER_MM;
}

/**
 * The vertical box one line of type occupies.
 *
 * pdfmake derives this from the font's ascender and descender; 1.35 is a
 * deliberate over-estimate covering the three standard families at the document
 * line height. Over-estimating costs a millimetre of white space, while
 * under-estimating clips the last line off a letterhead.
 */
function lineBox(fontSize: number): number {
  return fontSize * 1.35;
}

/**
 * Paper dimensions in points, portrait.
 *
 * Restated rather than imported: pdfmake does not export its table, and both the
 * band arithmetic here and the rules drawn in the header need the page width
 * before pdfmake has laid anything out. `satisfies` against the template's own
 * page-size union makes an unlisted size a compile error rather than a rule of
 * width `NaN`.
 */
const PAGE_POINTS = {
  A3: [841.89, 1190.55],
  A4: [595.28, 841.89],
  A5: [419.53, 595.28],
  B5: [498.9, 708.66],
  LETTER: [612, 792],
  LEGAL: [612, 1008],
  FOLIO: [612, 936],
} as const satisfies Record<PrintTemplateDocument['page']['size'], readonly [number, number]>;

/** The paper's width in points, orientation applied. */
export function pageWidth(document: PrintTemplateDocument): number {
  const [portrait, landscape] = PAGE_POINTS[document.page.size];
  return document.page.orientation === 'landscape' ? landscape : portrait;
}

/** The printable width between the left and right page margins, in points. */
export function contentWidth(document: PrintTemplateDocument): number {
  const { page } = document;
  return pageWidth(document) - mm(page.margin_left) - mm(page.margin_right);
}

/* ------------------------------------------------------------------------- *
 * Band heights
 * ------------------------------------------------------------------------- */

/**
 * The shortest header band that fits the letterhead the template describes.
 *
 * A PDF's page margins are fixed for the whole document, so the band that holds
 * the header has to be declared in advance — pdfmake will not grow it, and a
 * header taller than its band is simply overprinted by the first row of
 * content. That is not a hypothetical: it is what quietly dropped the "Printed"
 * date off this very letterhead the first time it rendered.
 *
 * So the template's `band` is treated as a floor the lab asks for, and this is
 * the floor the *content* demands; the renderer takes the larger. The builder
 * shows this number so the two never surprise each other.
 *
 * Returns millimetres, the unit the template is authored in.
 */
export function minimumHeaderBand(document: PrintTemplateDocument): number {
  const { header, theme, page } = document;
  if (!header.enabled) return page.margin_top;

  const labText =
    (header.lab_name === '' ? 0 : lineBox(theme.base_size * 1.9)) +
    header.lab_lines.filter((text) => text !== '').length * lineBox(theme.base_size * 0.9);

  const barcode = header.barcode.enabled
    ? mm(header.barcode.height) +
      (header.barcode.show_value ? lineBox(theme.base_size * 0.75) + 1 : 0)
    : 0;

  // The mark plus whatever text sits under it.
  const brand = (header.logo.enabled ? mm(header.logo.height) : 0) + labText;

  const fields = header.patient_fields.filter((field) => field.enabled).length;
  const rows = fields === 0 ? 0 : Math.ceil(fields / Math.min(header.patient_columns, fields));
  // One row of slack: a long referring doctor's name wraps to a second line in
  // a half-width column, and the band cannot grow on the one report where it
  // does.
  const grid = fields === 0 ? 0 : (rows + 1) * (lineBox(theme.base_size * 0.95) + mm(0.6));

  // Compact puts the identity fields beside the mark, so they share its row's
  // height instead of adding to it. This is the whole difference between the
  // two layouts, and it is why they are worth having both.
  const banner =
    header.layout === 'compact' && fields > 0
      ? Math.max(brand, grid, barcode)
      : Math.max(brand, barcode);

  const rule = header.rule ? mm(2) + mm(0.7) : 0;
  const stackedGrid = header.layout === 'compact' || fields === 0 ? 0 : mm(2.5) + grid;

  return toMm(mm(page.margin_top) + banner + rule + stackedGrid + mm(3));
}

/** The shortest footer band that fits the contact columns, page number and strip. */
export function minimumFooterBand(document: PrintTemplateDocument): number {
  const { footer, theme, page } = document;
  if (!footer.enabled) return page.margin_bottom;

  const rule = footer.rule ? mm(0.7) + mm(2) : 0;

  // The same test `footerColumns` applies, so the height reserved here and the
  // content actually drawn cannot disagree about which columns exist.
  const columns = footer.columns.filter(isFooterColumnFilled);
  const tallest = Math.max(0, ...columns.map((column) => column.lines.filter(Boolean).length));

  const blocks =
    columns.length === 0
      ? 0
      : lineBox(theme.base_size * 1.1) + tallest * lineBox(theme.base_size * 0.8) * 1.15;

  const pageNumber = footer.page_number.enabled ? mm(1.5) + lineBox(theme.base_size * 0.8) : 0;

  // Two lines of strip, not one: the wording wraps on a narrower page, and the
  // strip is the last thing on the sheet — content that overflows the footer
  // band has the paper edge to run into rather than white space.
  const strip =
    footer.strip.enabled && (footer.strip.text !== '' || footer.strip.note !== '')
      ? mm(1.5) + lineBox(theme.base_size * 0.8) * 2 + mm(1.6) * 2
      : 0;

  return toMm(rule + blocks + pageNumber + strip + mm(page.margin_bottom));
}

/** A footer column with something in it. Blank ones are not drawn. */
export function isFooterColumnFilled(
  column: PrintTemplateDocument['footer']['columns'][number],
): boolean {
  return column.heading !== '' || column.lines.some((text) => text !== '');
}

/**
 * The height of the signatory band at the foot of every page, in millimetres.
 *
 * Sized from the template, not from the people on this sheet: the tallest
 * block any enabled key could produce — the scan box, the caption, the name,
 * designation, qualifications, registration and timestamp. Reserved in the
 * bottom margin because pdfmake draws the band there and will not grow it.
 */
export function signatoryBand(document: PrintTemplateDocument): number {
  const { signatures } = document;
  const blocks = signatures.blocks.filter((block) => block.enabled);
  if (!signatures.enabled || blocks.length === 0) return 0;

  const size = signatures.size;
  const detail = lineBox(size * 0.85) * 1.15;

  const tallest = Math.max(
    ...blocks.map((block) => {
      const mark = block.show_image || signatures.rule ? mm(signatures.image_height) + mm(1.2) : 0;
      const caption = block.label === '' ? 0 : lineBox(size * 0.85) + mm(0.5);
      // Designation always; qualifications and registration when asked. One
      // line of slack under the name, which wraps when several people signed.
      const lines =
        1 +
        (block.show_qualifications ? 1 : 0) +
        (block.key === 'consultant' && block.show_registration ? 1 : 0);
      const timestamp =
        block.key === 'verified_by' && block.show_timestamp ? lineBox(size * 0.8) + mm(0.5) : 0;

      return mark + caption + lineBox(size) * 2 + lines * detail + timestamp;
    }),
  );

  return toMm(SIGNATORY_GAP + tallest + SIGNATORY_GAP);
}

/** The clear space above and below the signatory band, in points. */
export const SIGNATORY_GAP = mm(3);

/* ------------------------------------------------------------------------- *
 * Page margins
 * ------------------------------------------------------------------------- */

/** The four-value margin tuple pdfmake expects, in points. */
export type Margins = [number, number, number, number];

/**
 * The document's page margins.
 *
 * This is the calculation everything else hangs off: **`pageMargins` is what
 * reserves the header and footer bands**. Each band is the larger of what the
 * template asks for and what its own content needs, so "shrink the header"
 * never means "lose a line of it".
 *
 * A band that is turned off leaves its `blank_space` instead — the room a
 * pre-printed letterhead or footer already takes on the paper. The signatory
 * band sits above the footer, or above that blank space, and is reserved only
 * when the sheet has somebody to print in it.
 */
export function pageMarginsFor(
  document: PrintTemplateDocument,
  { hasSignatories }: { hasSignatories: boolean },
): Margins {
  const { page, header, footer } = document;

  const top = header.enabled
    ? mm(Math.max(header.band, minimumHeaderBand(document)))
    : px(header.blank_space);

  const bottom = footer.enabled
    ? mm(Math.max(footer.band, minimumFooterBand(document)))
    : px(footer.blank_space);

  return [
    mm(page.margin_left),
    top,
    mm(page.margin_right),
    bottom + (hasSignatories ? mm(signatoryBand(document)) : 0),
  ];
}

/* ------------------------------------------------------------------------- *
 * The registration slip
 * ------------------------------------------------------------------------- */

/**
 * The papers a slip prints on. Declared by `slipSizeSchema`, so the query
 * string, the picker and this arithmetic cannot disagree about what exists.
 *
 * Deliberately **not** `PrintTemplateDocument['page']['size']`. That union feeds
 * the template builder's paper dropdown and `PAGE_POINTS` above, which is a
 * fixed `[width, height]` pair — a continuous roll has a width and no height, so
 * it would have to be given an invented one. It also has no business being
 * offered as a paper for a clinical report: a results table with history columns
 * at 72 mm is not a document anybody wants.
 */
export type { SlipSize };

/**
 * How the slip is laid out, derived once and threaded through every section.
 *
 * The rule that keeps three papers from becoming three documents: **a section
 * may consult this object, never the requested size.** One array of content in
 * one order for all three, with the widths and the stacking chosen from here.
 */
export interface SlipGeometry {
  /** What pdfmake is given. `height: 'auto'` grows the page to fit its content. */
  page: 'A4' | 'A5' | { width: number; height: 'auto' };
  margins: [number, number, number, number];
  /** The printable width in points — what the tables and the barcode fit into. */
  content: number;
  /**
   * A continuous roll: one column, no footer band, no page numbers.
   *
   * pdfmake sets an auto-height page to `Infinity` internally, and a footer is
   * laid out at `pageHeight - footerHeight` — which pdfkit rejects with
   * "unsupported number: Infinity". So the roll has no footer band and puts the
   * contact line at the end of the content instead. That is the more faithful
   * shape anyway: a receipt has no bottom of page to pin anything to, it just
   * ends and the paper cuts.
   */
  continuous: boolean;
  fontSize: number;
  /**
   * The barcode's module width in millimetres, which is what a scanner reads.
   *
   * 0.33 mm on a sheet — the schema default, so the slip and the report cannot
   * drift on the one dimension that decides whether the symbol scans.
   *
   * 0.25 mm on the roll, because the symbol does not otherwise fit. A PIN is
   * `pin_2026-08-31-123456`, which Code 128 encodes as 244 modules; with the
   * mandatory quiet zones that is 264, and 264 x 0.33 mm is 87 mm against 72 mm
   * of printable paper. At 0.25 mm it is 66 mm. 0.25 mm is also exactly two dots
   * on a 203 dpi thermal head, so every bar is a whole number of dots — 0.33 mm
   * would be 2.64 and round unevenly bar by bar, which is the classic thermal
   * scan failure.
   */
  barcodeModuleMm: number;
  /** The QR module width in millimetres. Same argument, two dimensions. */
  qrModuleMm: number;
}

/** 80 mm thermal stock. ~72 mm prints on a 203 dpi head; the rest is feed. */
const THERMAL_WIDTH_MM = 80;
const THERMAL_GUTTER_MM = 4;

/**
 * The geometry for one slip.
 *
 * A **factory**, and that is not a style choice. pdfmake writes the resolved
 * height back onto the `pageSize` object it is handed, so a shared constant
 * takes the height of the first slip rendered in the process and then imposes it
 * on every later one as a fixed height — a thirty-test slip after a three-test
 * slip comes out as eight pages. Same hazard as the header and footer nodes,
 * which are rebuilt per page for the same reason.
 */
export function slipGeometry(size: SlipSize): SlipGeometry {
  if (size === 'THERMAL_80') {
    return {
      page: { width: mm(THERMAL_WIDTH_MM), height: 'auto' },
      // No bottom band to reserve: the contact line is part of the content.
      // A little tail margin so the cut does not shave the last line.
      margins: [mm(THERMAL_GUTTER_MM), mm(24), mm(THERMAL_GUTTER_MM), mm(6)],
      content: mm(THERMAL_WIDTH_MM - THERMAL_GUTTER_MM * 2),
      continuous: true,
      fontSize: 8,
      barcodeModuleMm: 0.25,
      qrModuleMm: 0.75,
    };
  }

  const gutter = size === 'A5' ? 8 : 12;
  const [width] = PAGE_POINTS[size];

  return {
    page: size,
    margins: [mm(gutter), mm(30), mm(gutter), mm(size === 'A5' ? 18 : 20)],
    content: width - mm(gutter) * 2,
    continuous: false,
    fontSize: 9,
    barcodeModuleMm: 0.33,
    qrModuleMm: 0.6,
  };
}
